/**
 * Toast - Individual toast notification component
 * Supports swipe-to-dismiss and auto-dismiss with progress bar
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { LiquidGlassContainer } from '../../components/liquid-glass-container';
import { Typography } from '../../components/typography';
import { SuccessFulIcon } from '../../icons/SuccessFulIcon';
import type { ToastItemProps, ToastVariant } from './types';
import styles from './Toast.module.css';

const ICONS: Record<ToastVariant, React.ReactNode> = {
  success: <SuccessFulIcon width={18} height={18}/>,
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
};

const SWIPE_THRESHOLD = 60;
const SWIPE_VELOCITY_THRESHOLD = 0.5;

export function Toast({ toast, onDismiss }: ToastItemProps) {
  const [state, setState] = useState<'entering' | 'visible' | 'exiting' | 'swiping'>('entering');
  const [progress, setProgress] = useState(100);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swipeOpacity, setSwipeOpacity] = useState(1);
  const duration = toast.duration ?? 5000;

  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);
  const isSwiping = useRef(false);

  const handleDismiss = useCallback(() => {
    if (state === 'exiting' || state === 'swiping') return;
    setState('exiting');
    setTimeout(() => onDismiss(toast.id), 200);
  }, [onDismiss, toast.id, state]);

  const handleSwipeDismiss = useCallback(() => {
    setState('swiping');
    setSwipeOffset(-100);
    setSwipeOpacity(0);
    setTimeout(() => onDismiss(toast.id), 150);
  }, [onDismiss, toast.id]);

  const handleClick = useCallback(() => {
    if (isSwiping.current) return;
    toast.onClick?.();
    handleDismiss();
  }, [toast.onClick, handleDismiss]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (state === 'exiting' || state === 'swiping') return;
    touchStartY.current = e.touches[0].clientY;
    touchStartTime.current = Date.now();
    isSwiping.current = false;
  }, [state]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (state === 'exiting' || state === 'swiping') return;

    const currentY = e.touches[0].clientY;
    const deltaY = touchStartY.current - currentY; // Positive = swiping up

    if (deltaY > 5) {
      isSwiping.current = true;
      setSwipeOffset(-deltaY);
      const opacity = Math.max(0, 1 - (deltaY / (SWIPE_THRESHOLD * 1.5)));
      setSwipeOpacity(opacity);
    } else if (deltaY < 0) {
      setSwipeOffset(Math.min(0, -deltaY * 0.3));
      setSwipeOpacity(1);
    }
  }, [state]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (state === 'exiting' || state === 'swiping') return;

    const endY = e.changedTouches[0].clientY;
    const deltaY = touchStartY.current - endY;
    const deltaTime = Date.now() - touchStartTime.current;
    const velocity = deltaY / deltaTime;

    if (deltaY > SWIPE_THRESHOLD || (deltaY > 20 && velocity > SWIPE_VELOCITY_THRESHOLD)) {
      handleSwipeDismiss();
    } else {
      setSwipeOffset(0);
      setSwipeOpacity(1);
      setTimeout(() => {
        isSwiping.current = false;
      }, 50);
    }
  }, [state, handleSwipeDismiss]);

  useEffect(() => {
    if (duration === 0) return;
    if (state === 'swiping') return;

    const startTime = Date.now();
    const endTime = startTime + duration;

    const updateProgress = () => {
      const now = Date.now();
      const remaining = Math.max(0, endTime - now);
      const newProgress = (remaining / duration) * 100;
      setProgress(newProgress);

      if (remaining <= 0) {
        handleDismiss();
      }
    };

    const interval = setInterval(updateProgress, 50);
    return () => clearInterval(interval);
  }, [duration, handleDismiss, state]);

  useEffect(() => {
    if (state === 'entering') {
      const timer = setTimeout(() => setState('visible'), 300);
      return () => clearTimeout(timer);
    }
  }, [state]);

  const animationClass = state === 'entering' ? styles.entering : state === 'exiting' ? styles.exiting : '';

  const swipeStyle = state === 'swiping' || swipeOffset !== 0 ? {
    transform: `translateY(${swipeOffset}px) scale(${1 - Math.abs(swipeOffset) * 0.001})`,
    opacity: swipeOpacity,
    transition: state === 'swiping' ? 'transform 0.15s ease-out, opacity 0.15s ease-out' : 'none',
  } : {};

  return (
    <LiquidGlassContainer
      variant="liquid"
      blur="soft"
      opacity="light"
      density="clear"
      radius="xl"
      reflex="minimal"
      className={`${styles.toast} ${styles[toast.variant]} ${animationClass}`}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={swipeStyle}
      as="div"
    >
      <div className={styles.iconContainer}>
        {ICONS[toast.variant]}
      </div>
      <div className={styles.content}>
        <Typography variant="body2" weight="semibold" lineHeight="17px">
          {toast.title}
        </Typography>
        {
          toast.message &&
          <Typography variant="body2" weight="normal" lineHeight="18px">
            {toast.message}
          </Typography>
        }
      </div>
    </LiquidGlassContainer>
  );
}


